import type {
  DataExportRequestDto,
  DataExportResponseDto,
  DataExportResultDto,
} from '../../types/api/stats';
import { BaseServiceClient } from '../BaseServiceClient';

interface StatsDataExportServiceOptions {
  userStore?: any;
  storage?: any;
}

export interface GetAllDataExportOptions extends StatsDataExportServiceOptions {
  blockSize?: number;
  /** Called after each block with the running row count. */
  onProgress?: (rowsLoaded: number) => void;
  /** Checked between blocks; when it returns true, paging stops with the rows loaded so far. */
  isCancelled?: () => boolean;
}

const EMPTY_DATA_EXPORT_RESPONSE: DataExportResponseDto = { blocknum: '', blocksize: '', results: [] };
const DEFAULT_BLOCK_SIZE = 500;
const MAX_BLOCKS = 200;

/** Stats > Data Export: record rows with data-variable values (data_export_get, 'data' channel). */
export class StatsDataExportService extends BaseServiceClient {
  constructor(apiService: any) {
    super(apiService);
  }

  /** Fetches one block of the export. */
  async getDataExport(
    request: DataExportRequestDto,
    options?: StatsDataExportServiceOptions
  ): Promise<DataExportResponseDto> {
    const response = await this.callService('data_export_get', request, 'data', {
      userStore: options?.userStore,
      storage: options?.storage,
    });

    const success = Boolean((response as any)?.success ?? (response as any)?.Success);
    if (!success) {
      throw new Error((response as any)?.message || (response as any)?.errorDescription || 'data_export_get failed');
    }

    const payload = this.unwrapPayload(response);
    const parsed = this.parseJsonPayload<DataExportResponseDto>(payload, EMPTY_DATA_EXPORT_RESPONSE);
    return { ...EMPTY_DATA_EXPORT_RESPONSE, ...parsed, results: parsed.results ?? [] };
  }

  /**
   * Pages through blocks (blocknum 1, 2, ...) until a block comes back short or empty, and
   * returns all rows concatenated. `request.blocknum` / `blocksize` are overridden.
   */
  async getAllDataExport(
    request: DataExportRequestDto,
    options?: GetAllDataExportOptions
  ): Promise<DataExportResultDto[]> {
    const blockSize = options?.blockSize ?? DEFAULT_BLOCK_SIZE;
    const rows: DataExportResultDto[] = [];

    for (let block = 1; block <= MAX_BLOCKS; block++) {
      if (options?.isCancelled?.()) break;
      const response = await this.getDataExport(
        { ...request, blocknum: String(block), blocksize: String(blockSize) },
        options
      );
      rows.push(...response.results);
      options?.onProgress?.(rows.length);
      if (response.results.length < blockSize) break;
    }

    return rows;
  }

  private unwrapPayload(response: unknown): unknown {
    return (response as any)?.payload
      ?? (response as any)?.Payload
      ?? (response as any)?.result
      ?? (response as any)?.Result
      ?? (response as any)?.data
      ?? (response as any)?.Data
      ?? response;
  }

  private parseJsonPayload<T>(payload: unknown, fallback: T): T {
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload) as T;
      } catch {
        return fallback;
      }
    }

    if (payload && typeof payload === 'object') {
      return payload as T;
    }

    return fallback;
  }
}
